import React from 'react';
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/Components/ui/button";
import { Calendar } from "@/Components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/Components/ui/popover";
import { CalendarIcon } from "lucide-react";

export interface DateAvailability {
    date: string;
    availability: 'plenty' | 'limited' | 'few' | 'none';
    available_slots: number;
    total_slots: number;
}

interface DatePickerProps {
    selected: Date | undefined;
    onSelect: (date: Date | undefined) => void;
    dateAvailability: DateAvailability[];
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    onMonthChange?: (date: Date) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({
    selected,
    onSelect,
    dateAvailability,
    isOpen,
    onOpenChange,
    onMonthChange
}) => {
    return (
        <Popover open={isOpen} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <Button
                    variant={"outline"}
                    className={cn(
                        "w-full justify-start text-left font-normal",
                        !selected && "text-muted-foreground"
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selected ? format(selected, "PPP") : <span>Pick a date</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={selected}
                    onSelect={onSelect}
                    onMonthChange={onMonthChange}
                    disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                    }}
                    className={cn(
                        "rounded-md border",
                        "[&_.plenty]:bg-green-100 [&_.plenty]:hover:bg-green-200",
                        "[&_.limited]:bg-yellow-100 [&_.limited]:hover:bg-yellow-200",
                        "[&_.few]:bg-orange-100 [&_.few]:hover:bg-orange-200",
                        "[&_.none]:bg-red-100 [&_.none]:hover:bg-red-200"
                    )}
                    classNames={{
                        day: "p-2 text-center rounded-md transition-colors",
                        day_selected: "bg-brand text-white hover:bg-brand-dark",
                        day_today: "bg-brand text-white",
                        day_disabled: "text-gray-400 hover:bg-transparent cursor-not-allowed",
                        day_outside: "text-gray-400",
                        day_range_middle: "rounded-none",
                        day_hidden: "invisible"
                    }}
                    modifiers={{
                        plenty: (date: Date) => {
                            const dateStr = format(date, 'yyyy-MM-dd');
                            const availability = dateAvailability.find(d => d.date === dateStr);
                            return availability?.availability === 'plenty';
                        },
                        limited: (date: Date) => {
                            const dateStr = format(date, 'yyyy-MM-dd');
                            const availability = dateAvailability.find(d => d.date === dateStr);
                            return availability?.availability === 'limited';
                        },
                        few: (date: Date) => {
                            const dateStr = format(date, 'yyyy-MM-dd');
                            const availability = dateAvailability.find(d => d.date === dateStr);
                            return availability?.availability === 'few';
                        },
                        none: (date: Date) => {
                            const dateStr = format(date, 'yyyy-MM-dd');
                            const availability = dateAvailability.find(d => d.date === dateStr);
                            return availability?.availability === 'none';
                        }
                    }}
                />
            </PopoverContent>
        </Popover>
    );
};

export default DatePicker; 